import type * as PI from '@sosocio/creator-types/project';
import { clonePageTemplateLayout } from 'tools/project/page-template-layout';

export interface PageTemplateChangeSnapshot {
	objectList: PI.PageObjectModel['id'][];
	objects: PI.PageObjectModel[];
	templateLayout: PI.PageModel['templateLayout'];
	templateSetId: PI.PageModel['templateSetId'];
}

export interface PageTemplateChangeRestoreDependencies {
	addObject: (objectModel: PI.PageObjectModel) => Promise<unknown> | unknown;
	deleteCurrentObjects: (
		objectTypes: PI.PageObjectModel['type'][],
	) => Promise<void>;
	getCurrentObjects: () => PI.PageObjectModel[];
	restorePageProperties: (
		properties: Pick<PI.PageModel, 'id'> & Partial<PI.PageModel>,
	) => void;
}

const PAGE_OBJECT_RUNTIME_RESOURCE_KEYS = [
	'_borderimage',
	'_canvas',
	'_image',
	'_loading',
	'_mask',
	'_previousState',
	'_vectorColors',
] as const satisfies ReadonlyArray<keyof PI.PageObjectModel>;

/**
 * Snapshot one page object while deliberately retaining its loaded render resources.
 *
 * @param objectModel Object to capture before template replacement.
 * @returns Detached mutable fields plus the original runtime resource handles.
 */
function clonePageObjectForTemplateChangeSnapshot(
	objectModel: PI.PageObjectModel,
): PI.PageObjectModel {
	const serializableObject = {
		...objectModel,
	};
	const runtimeResources = new Map<keyof PI.PageObjectModel, unknown>();

	PAGE_OBJECT_RUNTIME_RESOURCE_KEYS.forEach((key) => {
		if (Object.prototype.hasOwnProperty.call(
			objectModel,
			key,
		)) {
			runtimeResources.set(
				key,
				objectModel[key],
			);
			delete serializableObject[key];
		}
	});
	const clone = JSON.parse(JSON.stringify(serializableObject)) as PI.PageObjectModel;

	runtimeResources.forEach((value, key) => {
		Object.assign(
			clone,
			{
				[key]: value,
			},
		);
	});

	return clone;
}

/**
 * Capture the complete page state needed to undo a template replacement that
 * fails after its existing objects have already been removed.
 *
 * @param pageModel Page about to be mutated.
 * @param objectModels Current ordered page objects.
 * @returns Ordered immutable-field snapshot retaining loaded runtime resources.
 */
export function createPageTemplateChangeSnapshot(
	pageModel: PI.PageModel,
	objectModels: PI.PageObjectModel[],
): PageTemplateChangeSnapshot {
	return {
		objectList: [...pageModel.objectList ?? []],
		objects: objectModels.map(clonePageObjectForTemplateChangeSnapshot),
		templateLayout: pageModel.templateLayout
			? clonePageTemplateLayout(pageModel.templateLayout)
			: pageModel.templateLayout,
		templateSetId: pageModel.templateSetId,
	};
}

/**
 * Replace a partially mutated page with its exact pre-change object snapshot.
 *
 * @param pageModel Page whose failed replacement should be rolled back.
 * @param snapshot State captured before the replacement started.
 * @param dependencies Store-backed mutation operations.
 */
export async function restorePageTemplateChangeSnapshot(
	pageModel: PI.PageModel,
	snapshot: PageTemplateChangeSnapshot,
	dependencies: PageTemplateChangeRestoreDependencies,
): Promise<void> {
	const currentObjectTypes = [
		...new Set(
			dependencies.getCurrentObjects().map((objectModel) => objectModel.type),
		),
	];

	await dependencies.deleteCurrentObjects(currentObjectTypes);

	for (const objectModel of snapshot.objects) {
		await dependencies.addObject(objectModel);
	}

	dependencies.restorePageProperties({
		id: pageModel.id,
		objectList: [...snapshot.objectList],
		templateLayout: snapshot.templateLayout
			? clonePageTemplateLayout(snapshot.templateLayout)
			: snapshot.templateLayout,
		templateSetId: snapshot.templateSetId,
	});
}
